/**
 * Language selector component - shadcn/ui compatible
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Check, ChevronDown } from 'lucide-react';

import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from './tooltip';
import { SUPPORTED_LANGUAGES, getLanguageDirection, changeLanguage, persistKnownLanguage } from '../../i18n';
import { LANGUAGE_EXPLICIT_KEY } from '../../i18n/useGeoLanguageDefault';

interface LanguageSelectorProps {
  onLanguageChange?: (code: string) => void;
  /**
   * 'icon' (default): bare globe icon button — for dense chrome like the user menu.
   * 'labeled': outline pill showing the current language name — for auth/onboarding
   * panels where the control needs to be discoverable.
   */
  variant?: 'icon' | 'labeled';
}

export function LanguageSelector({
  onLanguageChange,
  variant = 'icon',
}: LanguageSelectorProps) {
  const { i18n, t } = useTranslation('common');

  // Update document direction and lang when language changes
  useEffect(() => {
    const direction = getLanguageDirection(i18n.language);
    document.documentElement.dir = direction;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const handleLanguageSelect = async (code: string) => {
    await changeLanguage(code);

    // Persist to the cross-subdomain cookie + localStorage so the choice follows
    // the user to every `*.panluma.ai` subdomain. Mark it explicit so geo-IP
    // auto-detection (useGeoLanguageDefault) stops overriding it on later visits.
    persistKnownLanguage(code);
    localStorage.setItem(LANGUAGE_EXPLICIT_KEY, '1');

    // Notify parent if callback provided
    onLanguageChange?.(code);
  };

  const currentLanguage =
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ||
    SUPPORTED_LANGUAGES[0];

  const menuItems = (
    <DropdownMenuContent align="end" className="min-w-[180px]">
      {SUPPORTED_LANGUAGES.map((lang) => {
        // The endonym is the primary line; `translatedName` is the same language
        // named in the READER's language. They are equal for a reader viewing
        // their own language, and then there is no second line to show.
        const translatedName = t(`common:languages.${lang.code}`);
        return (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageSelect(lang.code)}
            className="flex items-center justify-between"
          >
            <div className="flex flex-col">
              <span>{lang.nativeName}</span>
              {translatedName !== lang.nativeName && (
                <span className="text-xs text-muted-foreground">{translatedName}</span>
              )}
            </div>
            {lang.code === currentLanguage.code && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuContent>
  );

  if (variant === 'labeled') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-muted-foreground"
            aria-label={t('userMenu.language')}
          >
            <Languages className="h-4 w-4" />
            <span>{currentLanguage.nativeName}</span>
            <ChevronDown className="h-4 w-4 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        {menuItems}
      </DropdownMenu>
    );
  }

  return (
    <TooltipProvider>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('userMenu.language')}
              >
                <Languages className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('userMenu.language')}</p>
          </TooltipContent>
        </Tooltip>
        {menuItems}
      </DropdownMenu>
    </TooltipProvider>
  );
}
